//
// Mental Canvas controls class. Adapted primarily from MentalCanvas C++ code, also borrowing a little from
// three.js  OrbitControls.js to fit our needs.
//
// (c) 2016-2020 Mental Canvas Inc.

import * as $ from 'jquery';
import {
    EventDispatcher,
    MOUSE,
    PerspectiveCamera,
    Quaternion,
    Sphere,
    Vector2,
    Vector3,
    Scene
} from 'three';
import {
    _Math,
    parseBoolean,
    RBT
} from './utils';
import { MCScene } from './mcsrc';
import { ArcBall } from './MCArcball';
import { HandCamera } from './MCHand';
import { CameraConstraints } from './MCCameraConstraints';
import {
    ManipEvent,
    ManipEventType,
    ManipDelta
} from './MCDrawableTool';
import { GestureEvent } from './webkit';

//
// BE CAREFUL modifying this structure. It is - effectively - part of the C++ JSON export,
// and expects to be directly imported without type conversions from an MCX file.
//
export class ControlLimits {
    Elevation: boolean = true;
    ElevationMinDeg: number = -10;     // Degrees
    ElevationMaxDeg: number = 30;      // Degrees
    Azimuth: boolean = true;
    // Can look left/right by +/- 50% of this viewing range.
    AzimuthRangeDeg: number = 60;   // Degrees
    PanMax: number = 0.5;
    Pan: boolean = true;
    // TODO: add a "distance" to replace the old "center" parameter - i.e., a
    // distance along the bookmark look vector to use as the center.
    // Not part of the C++ json export
    center: Vector3 = new Vector3(0,0,0);

    clone(): ControlLimits {
        var result = new ControlLimits;
        result.copy(this);
        return result;
    }
    copy(rhs: ControlLimits) {
        var center = this.center;
        Object.assign(this, rhs);
        this.center = center;
        this.center.copy(rhs.center);
    }

    ElevationMinRad(): number { return this.ElevationMinDeg * _Math.DEG2RAD; }
    ElevationMaxRad(): number { return this.ElevationMaxDeg * _Math.DEG2RAD; }
    AzimuthRangeRad(): number { return this.AzimuthRangeDeg * _Math.DEG2RAD; }
    AzimuthMinRad(): number { return -this.AzimuthRangeRad()/2; }
    AzimuthMaxRad(): number { return this.AzimuthRangeRad()/2; }

    // This serves two purposes:
    // 1) Parsing URL arguments, which are all string-typed. Confirming valid values,
    //    doing type conversions, etc.
    // 2) Parsing older MCX files, in which the JSON was - for some reason - all string
    //    typed instead of anything sensible.
    //
    // Returns true if the key was relevant.
    parseString(key: string, value: string): boolean {
        // ControlLimits options parsing
        const boolKeys:string[] = ['Elevation','Azimuth','Pan'];
        const numberKeys:string[] = ['PanMax', 'ElevationMinDeg', 'ElevationMaxDeg', 'AzimuthRangeDeg'];
        const obsoleteDegKeys:string[] = ['ElevationMin','ElevationMax','AzimuthRange'];
        if (boolKeys.includes(key) && parseBoolean(value) !== undefined)
            // Older MCX files: use "1" and "0" as the values here.
            this[key] = parseBoolean(value);
        else if (numberKeys.includes(key))
            this[key] = parseFloat(value);
        else if (obsoleteDegKeys.includes(key))     // In use pre-v3
            this[key + 'Deg'] = parseFloat(value);
        else
            return false;
        return true;
        // TODO: center?
        // center cannot be specified via URL
    }
};

// This set of controls performs orbiting, dollying (zooming), and panning.
// Unlike TrackballControls, it maintains the "up" direction object.up (+Y by default).
//
//    Orbit - left mouse / touch: one finger move
//    Zoom - middle mouse, or mousewheel / touch: two finger spread or pinch
//    Pan - right mouse, or arrow keys / touch: two finger drag

enum STATE {
    NONE = -1,
    ROTATE = 0,
    DOLLY,
    PAN,
    TOUCH_DOLLYPAN
};

// Super-limited gesture detection system. Really only detects "single-finger single tap" vs. "other"
// right now - not yet sufficient to really detect swipes.
enum GESTURESTATE {
    INACTIVE = -2,      // No gesture detection in progress.
    UNKNOWN = -1,       // Unknown. Timer active.
    SWIPE,              // Not tap: single-finger swipe
    MULTITOUCH,         // Not tap: Multi-finger
    OTHER,              // Not tap: long-press? At any rate, more than 200ms elapsed.
    TAP                 // Tap!
};

const keyPanSpeed: number = 7.0; // pixels moved per arrow key push

class TrackpadDetectionInfo {
    m_startTime : number;
    m_deltas : number[] = [];
};

enum INPUTTYPE {
    MOUSE, TRACKPAD, TOUCH
};

export class MCTouchPoint {
    constructor(clientX : number, clientY : number, identifier: number = -1) {
        this.m_identifier = identifier;
        this.m_position = new Vector2(clientX, clientY).multiplyScalar(window.devicePixelRatio);
    }
    copyTouch(touch: Touch)
    {
        this.m_identifier = touch.identifier;
        this.m_position = new Vector2(touch.clientX, touch.clientY).multiplyScalar(window.devicePixelRatio);
    }
    m_identifier : number;
    m_position : Vector2;
}

export class MCControls extends EventDispatcher
{
    camera: PerspectiveCamera;
    private m_scene: MCScene;
    domElement: HTMLElement;
    viewportDims: Vector2;
    limits = new ControlLimits;
    constraintFrame = new RBT(new Quaternion, new Vector3);
    arcball: ArcBall;
    hand: HandCamera;
    m_cameraConstraints: CameraConstraints;
    // Vestigial - only for "classic" controls mode.
    m_arcballOrigCenter: Vector3 = new Vector3;
 
    // Set to true internally, in response to pointer/touch input.  Polled in the animate function to disable playback.
    m_bShouldHaltPlayback: boolean = false;
    m_bShouldTogglePlayback: boolean = false;

    m_bDisablePassiveConstraintEvaluation: boolean = true;
 
    enabled: boolean = true;
    enableZoom: boolean = true;

    // Note: if rotation is disabled, the finger/mouse mapping will change - instead of giving
    // preferred placement order as 1) rotation; 2) zoom; 3) pan
    // instead it will be: 1) pan; 2) zoom
    // i.e., one finger for pan
    enableRotate: boolean = true;
    enablePan: boolean = true;
    enableKeys: boolean = true;

    enableAuthoringNavMode: boolean = false;

    private m_bUseTouchEvents: boolean = false;
    private m_bUseMouseEvents: boolean = false;
    private m_bUseWebKitGestures: boolean = false;
    private m_bUsePointerEvents: boolean = false;

    // It takes a while to detect trackpad. When complete, this gets cleared to null.
    private m_trackpadDetection : TrackpadDetectionInfo = new TrackpadDetectionInfo;
    // Mousewheel or trackpad? Not known until detection complete.
    private m_bHasTrackpad : boolean = false;

    interactionInProgress(): boolean {
        return this.state !== STATE.NONE;
    }
    private state = STATE.NONE;

    private m_gestureState = GESTURESTATE.INACTIVE;
    private m_gestureTimeoutID : number;


    // Most recent set. May be spoofed points (for mouse). May be
    // higher-level like a centroid (for ms gesture codepath, where msgesture handles touches -> transform matrix for us)
    private m_touchPoints : MCTouchPoint[] = [];
    // For tap gesture and mouse zoom.
    private m_firstTouchPoint : MCTouchPoint;

    private calcTouchCentroid(touchPoints?: MCTouchPoint[]) : Vector2
    {
        if (touchPoints === undefined)
            touchPoints = this.m_touchPoints;

        var centroid : Vector2 = new Vector2(0,0);
        for (var touchPoint of touchPoints)
            centroid.add(touchPoint.m_position);
        return centroid.multiplyScalar(1 / touchPoints.length);
    }

    private lastRedrawRBT : RBT = null;

    boundingSphere: Sphere;
    depthBookmark: number;

    constructor(scene: MCScene, camera: PerspectiveCamera, domElement: HTMLElement)
    {
        super();
        this.m_scene = scene;
        this.camera = camera;
        this.domElement = domElement;
        this.viewportDims = new Vector2(domElement.clientWidth * window.devicePixelRatio, domElement.clientHeight * window.devicePixelRatio);
        this.arcball = new ArcBall(this.m_scene, this.camera, this.viewportDims, this.limits, this.constraintFrame);
        this.hand = new HandCamera(this.m_scene, this.camera, this.viewportDims);
        this.m_cameraConstraints = new CameraConstraints(this.camera);

        // iPadOS13... or, an older iOS with "request desktop site" activated.
        const bIPadOS13 : boolean = navigator.platform=='MacIntel' && navigator.maxTouchPoints>1;
        // Definitely have touch, definitely the primary input mode
        const bMobile : boolean = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent) || bIPadOS13;
        // Detects presence of the Touch Events API... but with caveats, no longer works in Chrome 70+
        const hasTouchEvents : boolean = ('ontouchstart' in window) ? true : false;
        const hasMSGestures : boolean = (typeof MSGesture != 'undefined') ? true : false;
        const hasWebKitGestures : boolean = ('GestureEvent' in window) ? true : false;

        if (hasMSGestures) {
            // i.e., Edge 17 and IE11
            console.log('Using pointer events');

            // W3C pointer events offer one advantage over touch events: on Edge 17/18, they detect finger
            // activity on the trackpad... sort of. (No dice on IE11, though.)
            //
            // See here: https://blogs.windows.com/msedgedev/2017/12/07/better-precision-touchpad-experience-ptp-pointer-events/
            // Trackpad pinch / rotate behave as expected - two pointer down events.
            // Trackpad two-finger pan is the weird one - it simulates a *single-finger* (one pointer down)
            // motion.
            this.m_bUsePointerEvents = true;
        }
        else if (hasWebKitGestures && !bMobile) {
            // i.e. Safari MacOS

            // Only use WebKit gestures if *not* on a touch device. They're useful for properly
            // processing *both* trackpad pinch-zoom and rotate gestures, while normal wheel
            // events only cover pinch-zoom alone.
            this.m_bUseWebKitGestures = true;
            this.m_bUseTouchEvents = hasTouchEvents;
            this.m_bUseMouseEvents = true;
            console.log('Using ' + (hasTouchEvents ? 'touch events, ' : '') + 'mouse events and WebKit gestures');
        }
        else if (hasTouchEvents) {
            // i.e. Firefox
            this.m_bUseTouchEvents = true;
            this.m_bUseMouseEvents = true;
            console.log('Using touch events and mouse events');
        }
        else {
            // i.e., Chrome

            // As of Chrome v70, the Touch Events API detection is disabled by default.
            // https://www.chromestatus.com/feature/4764225348042752
            // The events still fire, though - so we can add the handlers and use actual touch activity
            // to detect a touchscreen.
            // BE AWARE - if you have the Developer Tools open, the "mobile emulation" will be enabled, and
            // touch events *will* be detected. If you load the page first, *then* open Developer Tools,
            // you'll get the "normal" non-dev behavior.

            console.log('Using touch events (events not detected, yet.) and mouse events');

            // Show mouse messages, but switch to touch if they use their fingers.
            this.m_bUseTouchEvents = true;
            this.m_bUseMouseEvents = true;
        }
        this.updateInstructions(bMobile ? INPUTTYPE.TOUCH : INPUTTYPE.MOUSE);

        // Older browsers take a boolean for the third arg; newer ones take an options
        // struct. Detect which situation we have here.
        let bPassiveSupported = false;
        try {
            const options = <AddEventListenerOptions>{
                get passive() { // This function will be called when the browser
                                //   attempts to access the passive property.
                    bPassiveSupported = true;
                    return false;
                }
            };
        
            window.addEventListener('wheel', null, options);
            window.removeEventListener('wheel', null, options);
        } catch(err) {
            bPassiveSupported = false;
        }
        if (this.m_bUseMouseEvents) {
            this.domElement.addEventListener('contextmenu', this.onContextMenu, false);
            this.domElement.addEventListener('mousedown', this.onMouseDown, false);
            // passive:false is vital for trackpad pinch-zoom to work, as of Chrome 73:
            // https://developers.google.com/web/updates/2019/02/scrolling-intervention
            this.domElement.addEventListener('wheel', this.onWheelTrackpad,
                bPassiveSupported ? <AddEventListenerOptions>{passive: false} : false);
        }
        if (this.m_bUseTouchEvents) {
            this.domElement.addEventListener('touchstart', this.onTouchStart, false);
            this.domElement.addEventListener('touchend', this.onTouchEnd, false);
            this.domElement.addEventListener('touchmove', this.onTouchMove, false);
            this.domElement.addEventListener('touchcancel', this.onTouchCancel, false);
        }
        if (this.m_bUsePointerEvents) {
            this.domElement.addEventListener('pointerdown', this.onPointerDown, false);
            this.domElement.addEventListener('pointerup', this.onPointerUp, false);

            // We still need a way to zoom on IE11 trackpad devices. Two-finger vertical scroll
            // doesn't seem to trigger either pointer or wheel events.

            if (!/Edge\/\d./i.test(navigator.userAgent)) {
                this.domElement.addEventListener('contextmenu', this.onContextMenu, false);
                this.domElement.addEventListener('wheel', this.onWheelTrackpad,
                    bPassiveSupported ? <AddEventListenerOptions>{passive: false} : false);
            }
        }
        if(this.m_bUseWebKitGestures) {
            this.domElement.addEventListener('gesturestart',this.onWebKitTrackpadGestureStart, false);
            this.domElement.addEventListener('gesturechange',this.onWebKitTrackpadGestureChange, false);
            this.domElement.addEventListener('gestureend',this.onWebKitTrackpadGestureEnd, false);
        }

        window.addEventListener('keydown', this.onKeyDown, false);
    }

    // Typically, display the instructions for the *last used* input device.
    // The user may have multiple devices. Don't assume too much.
    private updateInstructions(inputType: INPUTTYPE) {
        var textRotate : string[][] = [['','','']], textNoRotate : string[][] = [['','','']], textEgo : string[][] = [['','','']];
        textRotate[INPUTTYPE.MOUSE] = ['Click and drag to rotate', 'Right-click drag to pan', 'Scroll to move in/out'],
        textRotate[INPUTTYPE.TRACKPAD] =  ['Drag to rotate', 'Two fingers to pan', 'Pinch to move in/out'],
        textRotate[INPUTTYPE.TOUCH] =  ['Drag to rotate', 'Two fingers to pan', 'Pinch to move in/out'],
        textNoRotate[INPUTTYPE.MOUSE] = ['Click and drag to pan', 'Scroll to move in/out'],
        textNoRotate[INPUTTYPE.TRACKPAD] = ['Drag to pan', 'Pinch to move in/out'],
        textNoRotate[INPUTTYPE.TOUCH] = ['Drag to pan', 'Pinch to move in/out'],
        textEgo[INPUTTYPE.MOUSE] = ['Click and drag to look around', 'Right-click drag to move sideways', 'Scroll to move in/out'],
        textEgo[INPUTTYPE.TRACKPAD] =  ['Drag to look around', 'Two fingers to move sideways', 'Pinch to move in/out'],
        textEgo[INPUTTYPE.TOUCH] =  ['Drag to look around', 'Two fingers to move sideways', 'Pinch to move in/out']
        this.m_scene.dispatchEvent({type:'updateInstructions',
            attachment:{text:(this.enableRotate ? (this.arcball.bEgocentric ? textEgo : textRotate)
                                          : textNoRotate)[inputType], rotate: this.enableRotate},
                        inputType: inputType});
    }
    OnResize() {
        this.viewportDims.set(this.domElement.clientWidth * window.devicePixelRatio, this.domElement.clientHeight * window.devicePixelRatio);
    }
 
    animateRubberBand(deltatime: number): boolean
    {
        // this.dispatchEvent({type: 'updateActiveBookmark'});
        this.updateActiveBookmark();
        const bInteractionInProgress = this.state !== STATE.NONE || this.m_bWheelTrackpadInProgress;
        let bRestartConstraints = false;
        if (bInteractionInProgress && this.m_bDisablePassiveConstraintEvaluation) {
            // Interaction in progress: start evaluating constraints from here onwards.
            this.m_bDisablePassiveConstraintEvaluation = false;
            bRestartConstraints = true;
        }

        if (this.m_bDisablePassiveConstraintEvaluation)
            return false;

        this.m_cameraConstraints.apply(deltatime, bInteractionInProgress, bRestartConstraints);

        // Only apply rubber-band effect when all fingers released.
        // Rubber band effect part #2: releasing the band.
        // If the rotation position is beyond the limits and the user has let go, move the camera back to the limit.
        this.arcball.ApplyConstraints(bInteractionInProgress ? 'rollrubberband' : 'rubberband', deltatime, undefined);
        return this.notifyIfCameraMoved();
    }
    updateActiveBookmark() {
        const EPS = 0.000001;
        if (this.lastRedrawRBT != null &&
            this.lastRedrawRBT.translation.distanceToSquared(this.camera.position) < EPS &&
            8 * (1 - this.lastRedrawRBT.rotation.dot(this.camera.quaternion)) < EPS)
            return;

            // Cache RBT for next round of testing.
            if (this.lastRedrawRBT == null)
                this.lastRedrawRBT = new RBT(this.camera.quaternion, this.camera.position);
            else {
                this.lastRedrawRBT.translation.copy(this.camera.position);
                this.lastRedrawRBT.rotation.copy(this.camera.quaternion);
            }
            this.dispatchEvent( { type: 'updateActiveBookmark' });
        return
    }
    notifyIfCameraMoved(): boolean {
        // update condition is:
        // min(camera displacement, camera rotation in radians)^2 > EPS
        // using small-angle approximation cos(x/2) = 1 - x^2 / 8
        const EPS = 0.000001;
        if (this.lastRedrawRBT != null &&
            this.lastRedrawRBT.translation.distanceToSquared(this.camera.position) < EPS &&
            8 * (1 - this.lastRedrawRBT.rotation.dot(this.camera.quaternion)) < EPS)
            return false;

            // Cache RBT for next round of testing.
            if (this.lastRedrawRBT == null)
                this.lastRedrawRBT = new RBT(this.camera.quaternion, this.camera.position);
            else {
                this.lastRedrawRBT.translation.copy(this.camera.position);
                this.lastRedrawRBT.rotation.copy(this.camera.quaternion);
            }
            this.dispatchEvent( { type: 'cameramove' });
            
        return true;
    }
    dispose() {
        if (this.m_bUseMouseEvents) {
            this.domElement.removeEventListener( 'contextmenu', this.onContextMenu, false );
            this.domElement.removeEventListener( 'mousedown', this.onMouseDown, false );
            this.domElement.removeEventListener( 'wheel', this.onWheelTrackpad, false );
            this.domElement.removeEventListener( 'wheel', this.onWheelTrackpad, <AddEventListenerOptions>{ passive: false } );
            document.removeEventListener( 'mousemove', this.onMouseMove, false );
            document.removeEventListener( 'mouseup', this.onMouseUp, false );
            document.body.removeEventListener( 'mouseleave', this.onMouseLeave, false );
        }
        if (this.m_bUseTouchEvents) {
            this.domElement.removeEventListener('touchstart', this.onTouchStart, false);
            this.domElement.removeEventListener('touchend', this.onTouchEnd, false);
            this.domElement.removeEventListener('touchmove', this.onTouchMove, false);
            this.domElement.removeEventListener('touchcancel', this.onTouchCancel, false);
        }
        if (this.m_bUsePointerEvents) {
            this.domElement.removeEventListener('pointerdown', this.onPointerDown, false);
            this.domElement.removeEventListener('pointermove', this.onPointerMove, false);
            this.domElement.removeEventListener('pointerup', this.onPointerUp, false);
        }
        this.m_scene = null;
        this.arcball.dispose();
        this.arcball = null;
        this.hand.dispose();
        this.hand = null;
        window.removeEventListener( 'keydown', this.onKeyDown, false );
    }

    //
    // event callbacks - update the object state
    //
    onKeyDown = ( event: KeyboardEvent ) => {
        if ( this.enabled === false || this.enableKeys === false || this.enablePan === false ) return;

        this.m_bShouldHaltPlayback = true; // Tell the main animate func we want to halt playback.

        // The four arrow keys
        enum keys {
            LEFT= 37, UP= 38, RIGHT= 39, DOWN= 40
        };

        var translate = new Vector2(0, 0); // Don't change panstart in case key & mouse both down.
        //console.log( 'onKeyDown' );
        // TODO: should probably prioritize rotation over panning, for consistency with other inputs

        // Note inversion here. This is "egocentric" movement - which is what feels most natural for
        // keyboard interaction (e.g., like a first-person shooter)
        switch ( event.keyCode ) {
        case keys.UP:
            translate.set(0, keyPanSpeed);
            break;
        case keys.DOWN:
            translate.set(0, -keyPanSpeed);
            break;
        case keys.LEFT:
            translate.set(keyPanSpeed, 0);
            break;
        case keys.RIGHT:
            translate.set(-keyPanSpeed, 0);
            break;
        default:
            return;
        }

        // TODO: invert if egocentric flag set

        this.m_touchPoints = [ new MCTouchPoint(this.domElement.clientWidth / 2, this.domElement.clientHeight / 2) ];
        this.m_firstTouchPoint = this.m_touchPoints[0];
        this.hand.ProcessEvent(new ManipEvent(event.timeStamp, ManipEventType.ET_Starting, new Vector2(0, 0),
            null, false, true));
        this.hand.ProcessEvent(new ManipEvent(event.timeStamp, ManipEventType.ET_Started, this.m_touchPoints[0].m_position,
            null, false, true));
        const centroid : Vector2 = this.m_firstTouchPoint.m_position;
        const rotateRad : number = 0; 
        const scale : number = 1;

        this.hand.ProcessEvent(new ManipEvent(event.timeStamp, ManipEventType.ET_Moving, centroid,
            new ManipDelta(rotateRad,
                centroid,
                translate.clone().negate(),
                1 / scale),
            false, true));
        this.notifyIfCameraMoved();
        this.hand.ProcessEvent(new ManipEvent(event.timeStamp, ManipEventType.ET_Completed,
            new Vector2(0, 0), null, false, true));
        this.dispatchEvent({ type: 'interactioncomplete' });

        this.m_touchPoints = [];
        this.m_firstTouchPoint = null;
    }

    startGestureDetection()
    {
        this.m_gestureState = GESTURESTATE.UNKNOWN;
        this.m_gestureTimeoutID = window.setTimeout(this.onGestureDetectTimeout, 200);
    }
    endGestureDetection(result: GESTURESTATE)
    {
        if (this.m_gestureState !== GESTURESTATE.UNKNOWN)
            return;

        this.m_gestureState = GESTURESTATE.INACTIVE;
        window.clearTimeout(this.m_gestureTimeoutID);
        this.m_gestureTimeoutID = -1;
        if (result === GESTURESTATE.TAP)
            this.onSingleTapGesture( this.m_firstTouchPoint.m_position );
        else
            this.m_bShouldHaltPlayback = true;
    }
    onGestureDetectTimeout = () => {
        this.endGestureDetection(GESTURESTATE.OTHER);
    }
    onSingleTapGesture = (pos: Vector2) => {
        //console.log("Tap");
        if (this.m_scene.hasPickableCanvases())
            this.m_scene.enqueuePick( pos.x / window.devicePixelRatio, pos.y / window.devicePixelRatio );
        else
            this.m_bShouldTogglePlayback = true;
    }
    //
    // This codepath handles actual mouse and touchpad events.
    // - If touch events are present, that codepath (ontouchmove) explicitly prevents mousedown from triggering
    // - If MS Gestures are in use, that codepath explicitly prevents mousedown from triggering
    //
    onMouseDown = (event: MouseEvent) => {
        //console.log("onMouseDown");
        if ( this.enabled === false ) return;
        // TODO: handle multi-button cases better
        if ( this.state !== STATE.NONE ) {
            if (this.m_gestureState === GESTURESTATE.UNKNOWN)
                this.endGestureDetection(GESTURESTATE.OTHER);
            return;
        }

        event.preventDefault();

        this.m_touchPoints = [ new MCTouchPoint(event.clientX, event.clientY) ];
        this.m_firstTouchPoint = this.m_touchPoints[0];

        if ( event.button === MOUSE.LEFT ) {
            if (this.enableRotate) {
                this.state = STATE.ROTATE;
                this.arcball.ProcessTouchDownEvent(this.m_touchPoints[0].m_position);
            } else if (this.enablePan) {
                this.hand.ProcessEvent(new ManipEvent(event.timeStamp, ManipEventType.ET_Starting, new Vector2(0, 0)));
                this.hand.ProcessEvent(new ManipEvent(event.timeStamp, ManipEventType.ET_Started, this.m_touchPoints[0].m_position));
                this.state = STATE.PAN;
            }
        } else if ( event.button === MOUSE.MIDDLE ) {
            if ( this.enableZoom === false ) return;
            const centroid : Vector2 = this.m_touchPoints[0].m_position;
            this.hand.ProcessEvent(new ManipEvent(event.timeStamp, ManipEventType.ET_Starting, new Vector2(0, 0)));
            this.hand.ProcessEvent(new ManipEvent(event.timeStamp, ManipEventType.ET_Started, centroid));
            this.state = STATE.DOLLY;
        } else if ( event.button === MOUSE.RIGHT ) {
            if ( this.enablePan === false ) return;
            this.hand.ProcessEvent(new ManipEvent(event.timeStamp, ManipEventType.ET_Starting, new Vector2(0, 0)));
            this.hand.ProcessEvent(new ManipEvent(event.timeStamp, ManipEventType.ET_Started, this.m_touchPoints[0].m_position));
            this.state = STATE.PAN;
        }

        if (this.state === STATE.NONE) {
            this.m_touchPoints = [];
            this.m_firstTouchPoint = null;
        } else {
            if ( event.button === MOUSE.LEFT )
                this.startGestureDetection();

            // By handling events at the document level, we're effectively doing a type of mousecapture.
            // Unfortunately, that capture fails once the cursor leaves the iframe.
            document.addEventListener( 'mousemove', this.onMouseMove, false );
            document.addEventListener( 'mouseup', this.onMouseUp, false );
            // Safari requires this to be on document.body, for whatever reason.
            document.body.addEventListener( 'mouseleave', this.onMouseLeave, false );
        }
    }

    onMouseMove = ( event: MouseEvent ) => {

        // "No buttons" test: this gives a particular behavior when in an iframe:
        // if the mouse is down, leaves the iframe, and returns into the iframe,
        // then:
        //   a) if the button is still down, keep going as if nothing happened
        //   b) if the button was released before re-entering, stop the prior movement.
        // This still isn't ideal. Really, we want to capture events until
        // the button comes up, but it'll take some research to figure out how to
        // do that portably while in an iframe.
        var bNoButtons:boolean = false;
        if ( event.buttons === 0 )
            bNoButtons = true;
        // Safari special case. Doesn't support buttons, does support which.
        if (typeof event.buttons == "undefined" && event.which === 0)
            bNoButtons = true;
        if (bNoButtons) {
            this.onMouseCancel(event);
            return;
        }

        event.preventDefault();

        const prevTouchPoints = this.m_touchPoints;
        this.m_touchPoints = [ new MCTouchPoint(event.clientX, event.clientY) ];
        if (this.state === STATE.ROTATE) {
            this.arcball.ProcessTouchMoveEvent(this.m_touchPoints[0].m_position);
        } else if ( this.state === STATE.DOLLY ) {
            // Always centered on the *initial* position.
            const centroid : Vector2 = this.m_firstTouchPoint.m_position;
            const rotateRad : number = 0; 
            const translate : Vector2 = new Vector2(0,0);
            const scale : number = 1 + .001 * (this.m_touchPoints[0].m_position.y - prevTouchPoints[0].m_position.y);

            this.hand.ProcessEvent(new ManipEvent(event.timeStamp, ManipEventType.ET_Moving, centroid,
                new ManipDelta(rotateRad,
                    centroid,
                    translate.clone().negate(),
                    1 / scale),
                false, false));
        } else if ( this.state === STATE.PAN ) {
            const lastCentroid : Vector2 = prevTouchPoints[0].m_position;
            const centroid : Vector2 = this.m_touchPoints[0].m_position;
            const translate : Vector2 = centroid.clone().sub(lastCentroid); ///pchange
            const rotateRad : number = 0;
            const scale : number = 1;

            this.hand.ProcessEvent(new ManipEvent(event.timeStamp, ManipEventType.ET_Moving, centroid,
                new ManipDelta(rotateRad,
                    centroid,
                    translate.clone().negate(),
                    1 / scale),
                false, false));
        }

        if (this.m_gestureState == GESTURESTATE.UNKNOWN &&
            this.m_touchPoints[0].m_position.distanceTo(this.m_firstTouchPoint.m_position) > 20)
            this.endGestureDetection(GESTURESTATE.SWIPE);

        this.notifyIfCameraMoved();
    }

    onMouseUp = ( event: MouseEvent ) => {
        switch (this.state) {
            case STATE.NONE:
                break;
            case STATE.ROTATE:
                this.arcball.ProcessTouchUpEvent(new Vector2(0,0));
                break;
            default:
                this.hand.ProcessEvent(new ManipEvent(event.timeStamp, ManipEventType.ET_Completed, new Vector2(0, 0)));
                break;
        }
        document.removeEventListener( 'mousemove', this.onMouseMove, false );
        document.removeEventListener( 'mouseup', this.onMouseUp, false );
        document.body.removeEventListener( 'mouseleave', this.onMouseLeave, false );
        event.preventDefault();

        this.state = STATE.NONE;
        if (this.m_gestureState === GESTURESTATE.UNKNOWN)
            // Nothing showed up to tell us this *wasn't a tap* - and we just got mouse up. So it's a tap.
            this.endGestureDetection(GESTURESTATE.TAP);

        // Don't reset gesturestate. Tap tester relies on it remaining as "SWIPE" if that happened
        this.m_touchPoints = [];
        this.m_firstTouchPoint = null;

        this.dispatchEvent({type:'interactioncomplete'});
    }

    onMouseLeave = ( event: MouseEvent ) => {
        //console.log("onMouseLeave");
    }

    onMouseCancel = ( event: MouseEvent ) => {

        document.removeEventListener( 'mousemove', this.onMouseMove, false );
        document.removeEventListener( 'mouseup', this.onMouseUp, false );
        document.body.removeEventListener( 'mouseleave', this.onMouseLeave, false );

        this.state = STATE.NONE;
        this.endGestureDetection(GESTURESTATE.INACTIVE);
    }

    
    private m_bWheelTrackpadInProgress : boolean = false;
    private m_wheelTrackpadCompleteTimer : number = -1;

    // For both:
    // - physical mouse: mousewheel
    // - trackpad: two-finger scroll (H or V) without press, and two-finger pinch
    onWheelTrackpad = ( event: WheelEvent ) => {
        if (this.enabled === false || this.enableZoom === false ||
            (!this.m_bWheelTrackpadInProgress && this.state !== STATE.NONE && this.state !== STATE.ROTATE)) return;

        event.preventDefault();
        event.stopPropagation();

        this.m_bShouldHaltPlayback = true; // Tell the main animate func we want to halt playback.

        if (this.m_trackpadDetection !== null) {
            // There are many ways to test for the presence of a trackpad. Some sources expect trackpads
            // to deliver events at a faster rate, but we're not seeing that. Instead, assume that mousewheel
            // will deliver deltas in fixed large increments (e.g., 120) while trackpad offers a wider spectrum
            // of deltas. Once we've accumulated enough deltas to test, do so. Also assume that mousewheel is
            // vertical only, and never sends ctrl key (hint for pinch gesture on multitouch trackpads).

            // deltaX is a pretty good indicator of a (good) trackpad. It'll take quite a bit of
            // convincing to persuade us otherwise.
            if (event.deltaX != 0 || event.ctrlKey) {
                this.m_bHasTrackpad = true;
                this.m_trackpadDetection = null;
                console.log("Mouse type detection: trackpad*");
                this.updateInstructions(INPUTTYPE.TRACKPAD);
            } else {
                if (this.m_trackpadDetection.m_deltas.length == 0)
                    this.m_trackpadDetection.m_startTime = event.timeStamp;
                var deltas = this.m_trackpadDetection.m_deltas;
                deltas.push(Math.abs(event.deltaY));

                if (deltas.length >= 6) {
//                if(event.timeStamp - this.m_trackpadDetection.m_startTime >= 100) {
                    deltas.sort((a,b) => a-b);
                    const minDelta : number = deltas[0];
                    const maxDelta : number = deltas[deltas.length - 1];
                    const medianDelta : number = deltas[Math.floor(deltas.length/2)]
                    // The only indicator we've found so far is a rapid-fire set of wheel events - 6 in 50ms.
                    // It's possible to do that with mousewheel, but takes an effort.
                    this.m_bHasTrackpad = (event.timeStamp - this.m_trackpadDetection.m_startTime) < 50;
                    //minDelta < 40 || maxDelta / minDelta > 1.5;
                    //console.log("dt = " + (event.timeStamp - this.m_trackpadDetection.m_startTime) + " count = " + this.m_trackpadDetection.m_deltas.length);
                    //console.log("deltas = " + minDelta + "," + maxDelta + "," + medianDelta);
                    console.log("Mouse type detection: " + (this.m_bHasTrackpad ? "trackpad" : "mouse"));
                    this.m_trackpadDetection = null;

                    // Update instructions to reflect most recently used input device.
                    this.updateInstructions(this.m_bHasTrackpad ? INPUTTYPE.TRACKPAD : INPUTTYPE.MOUSE);
                }
            }
        }
        else if (event.deltaX != 0 && !this.m_bHasTrackpad) {
            // If we *ever* get deltax - even midway through multiple wheels - change our mind about
            // trackpad.
            // TODO: wait until a certain delay without wheel events, to avoid changing behavior
            // midscroll (e.g., from zoom to pan!)
            this.m_bHasTrackpad = true;
            this.m_trackpadDetection = null;
            console.log("Mouse type belated detection: trackpad");
            this.updateInstructions(INPUTTYPE.TRACKPAD);
        }
        // Swallow events until detection is complete
        if (this.m_trackpadDetection !== null)
            return;

        if(!this.m_bWheelTrackpadInProgress) {
            this.m_touchPoints = [ new MCTouchPoint(event.clientX, event.clientY) ];
            this.m_firstTouchPoint = this.m_touchPoints[0];
            this.hand.ProcessEvent(new ManipEvent(event.timeStamp, ManipEventType.ET_Starting, new Vector2(0, 0),
                null, false, true));
            this.hand.ProcessEvent(new ManipEvent(event.timeStamp, ManipEventType.ET_Started, this.m_touchPoints[0].m_position,
                null, false, true));
            this.m_bWheelTrackpadInProgress = true;
        } else {
            window.clearTimeout(this.m_wheelTrackpadCompleteTimer);
        }
        const centroid : Vector2 = this.m_firstTouchPoint.m_position;
        const rotateRad : number = 0;
        var translate : Vector2 = new Vector2(0,0);
        var scale : number = 1;
        const DOM_DELTA_PIXEL = 0x00, DOM_DELTA_LINE = 0x01, DOM_DELTA_PAGE = 0x02;
        // Firefox: deltaMode = DOM_DELTA_LINE.
        // Everyone else: deltaMode = DOM_DELTA_PIXEL
        const deltaToPixels = (event.deltaMode == DOM_DELTA_LINE ? 12 : event.deltaMode == DOM_DELTA_PAGE ? 600 : 1);
        if (this.m_bHasTrackpad) {
            if (event.ctrlKey) {
                // Ctrl is how trackpad pinch-zoom is signalled. See
                // https://medium.com/@auchenberg/detecting-multi-touch-trackpad-gestures-in-javascript-a2505babb10e
                // For trackpad, event.preventDefault() + passive:false are vital to ensuring that the pinch-zoom doesn't then
                // zoom the page itself.
                // As of Chrome 73:
                // https://developers.google.com/web/updates/2019/02/scrolling-intervention
                scale += 0.003 * -event.deltaY * deltaToPixels;
                this.state = STATE.DOLLY;
            }
            else {
                translate.set(-event.deltaX, -event.deltaY).multiplyScalar(window.devicePixelRatio * deltaToPixels); /// pchange
                this.state = STATE.PAN;
            }
        } else {
            scale += 0.001 * event.deltaY * deltaToPixels;
            this.state = STATE.DOLLY;
        }
 
        this.hand.ProcessEvent(new ManipEvent(event.timeStamp, ManipEventType.ET_Moving, centroid,
            new ManipDelta(rotateRad,
                centroid,
                translate.clone().negate(),
                1 / scale),
            false, true));
        this.notifyIfCameraMoved();

        // If no further wheel events within 100ms, then send a complete message.
        //
        // On some browsers (Safari MacOS), many wheel events are dispatched per frame. Want to minimize work
        // done, and recomputing navmode center is too expensive to do every frame. Wheel events appear to pre-empt
        // requestAnimationFrame() if there's a shortage of CPU time.
        //
        // Don't make this too long. The rubber-band effect for pan limits won't kick in until:
        //   1) all inertia wheel events are processed   (trackpad)
        //   2) this timer completes
        this.m_wheelTrackpadCompleteTimer = window.setTimeout(this.wheelTrackpadComplete, 100, event.timeStamp);
    }
    wheelTrackpadComplete = (timestamp: number) => {
        this.hand.ProcessEvent(new ManipEvent(timestamp, ManipEventType.ET_Completed,
            new Vector2(0, 0), null, false, true));
        this.state = STATE.NONE;
        this.dispatchEvent({type:'interactioncomplete'});

        this.m_touchPoints = [];
        this.m_firstTouchPoint = null;
        this.m_bWheelTrackpadInProgress = false;
        this.m_wheelTrackpadCompleteTimer = -1;
    }

    //
    // This codepath handles touchscreen events on most browsers (Chrome, Safari, Firefox).
    // Edge (default config) and IE11 don't trigger these events.
    //
    onTouchStart = ( event: TouchEvent ) => {
        //console.log("onTouchStart");
        if ( this.enabled === false ) return;
        // Prevent MouseDown event
        event.preventDefault();
        this.m_touchPoints = [];
        for (var touch of event.touches)
            this.m_touchPoints.push(new MCTouchPoint.prototype.copyTouch(touch));

        this.onTouchStart_Impl(event.timeStamp);

        // Now that we've detected touch, display the right cues to user.
        // (Primarily for Chrome 70+)
        // Update instructions to reflect most recently used input device
        this.updateInstructions(INPUTTYPE.TOUCH);
    }

    // After converting from native touchevent to MCTouchPoints in this.m_touchPoints.
    onTouchStart_Impl( timeStamp : number)
    {
        const bOneFingerPan : boolean = this.enablePan && !this.enableRotate;
        if (this.m_touchPoints.length == 1) {
            if ( this.enableRotate ) {
                this.state = STATE.ROTATE;
                this.arcball.ProcessTouchDownEvent(this.m_touchPoints[0].m_position);
            } else if (bOneFingerPan) {
                this.state = STATE.PAN;
                this.hand.ProcessEvent(new ManipEvent(timeStamp, ManipEventType.ET_Starting, new Vector2(0, 0)));
                this.hand.ProcessEvent(new ManipEvent(timeStamp, ManipEventType.ET_Started, this.m_touchPoints[0].m_position));
            }
            this.m_firstTouchPoint = this.m_touchPoints[0];
        } else if (this.m_touchPoints.length > 1) {
            const centroid : Vector2 = this.calcTouchCentroid();
            if (this.enableZoom)
                this.state = bOneFingerPan ? STATE.DOLLY : STATE.TOUCH_DOLLYPAN;
            else if ( this.enablePan && !bOneFingerPan )
                this.state = STATE.PAN;
            this.hand.ProcessEvent(new ManipEvent(timeStamp, ManipEventType.ET_Starting, new Vector2(0, 0)));
            this.hand.ProcessEvent(new ManipEvent(timeStamp, ManipEventType.ET_Started, centroid));
        }

        if ( this.state === STATE.NONE ) {
            this.m_touchPoints = [];
            this.m_firstTouchPoint = null;
        } else if (this.m_touchPoints.length == 1) {
            this.startGestureDetection();
        } else {
            if (this.m_gestureState == GESTURESTATE.UNKNOWN)
                this.endGestureDetection(GESTURESTATE.MULTITOUCH);
        }
    }

    onTouchMove = ( event: TouchEvent ) => {
        if ( this.enabled === false ) return;

        event.preventDefault();
        event.stopPropagation();

        const prevTouchPoints : MCTouchPoint[] = this.m_touchPoints;
        this.m_touchPoints = [];
        for (var touch of event.touches)
            this.m_touchPoints.push(new MCTouchPoint.prototype.copyTouch(touch));

        this.onTouchMove_Impl(prevTouchPoints, event.timeStamp)
    }

    onTouchMove_Impl(prevTouchPoints: MCTouchPoint[], timeStamp: number)
    {
        const prevCentroid : Vector2 = this.calcTouchCentroid(prevTouchPoints);
        if( this.m_touchPoints.length == 1) {
            if ( this.state === STATE.ROTATE )
                this.arcball.ProcessTouchMoveEvent(this.m_touchPoints[0].m_position);
            else if (this.state == STATE.PAN) {
                const centroid : Vector2 = this.m_touchPoints[0].m_position;
                const translate : Vector2 = centroid.clone().sub(prevCentroid); // pchange
                const rotateRad : number = 0;
                const scale : number = 1;
 
                this.hand.ProcessEvent(new ManipEvent(timeStamp, ManipEventType.ET_Moving, centroid,
                    new ManipDelta(rotateRad,
                        centroid,
                        translate.clone().negate(),
                        1 / scale),
                    false, false));
            }
        } else if (this.m_touchPoints.length > 1) {
            // TODO: handle for >2 fingers down
            if(prevTouchPoints[0].m_identifier != this.m_touchPoints[0].m_identifier)
                prevTouchPoints.reverse();

            // TODO: use other points also for >2 fingers down.
            const prevDir : Vector2 = prevTouchPoints[1].m_position.clone().sub(prevTouchPoints[0].m_position);

            const centroid : Vector2 = this.calcTouchCentroid();
            const dir : Vector2 = this.m_touchPoints[1].m_position.clone().sub(this.m_touchPoints[0].m_position);

            const scale : number = dir.length() / prevDir.length();
            // Normalizing is not required - atan2 will still work, as both y and x args include both of these vectors' lengths.
            const rotateRad : number = Math.atan2(prevDir.cross(dir), prevDir.dot(dir)); 
            const translate : Vector2 = centroid.clone().sub(prevCentroid); /// pchange

            this.hand.ProcessEvent(new ManipEvent(timeStamp, ManipEventType.ET_Moving, centroid,
                new ManipDelta(rotateRad,
                    centroid,
                    translate.clone().negate(),
                    1 / scale),
                false, false));
        }

        if (this.m_gestureState == GESTURESTATE.UNKNOWN &&
            this.m_touchPoints[0].m_position.distanceTo(this.m_firstTouchPoint.m_position) > 20)
            this.endGestureDetection(GESTURESTATE.SWIPE);

        this.notifyIfCameraMoved();
    }

    onTouchEnd = ( event: TouchEvent ) => {
        if ( this.enabled === false ) return;
        // Prevent MouseUp event
        event.preventDefault();
        this.m_touchPoints = [];
        for (var touch of event.touches)
            this.m_touchPoints.push(new MCTouchPoint.prototype.copyTouch(touch));

        this.onTouchEnd_Impl(event.timeStamp);
    }

    onTouchEnd_Impl(timeStamp : number)
    {
        if (this.m_touchPoints.length <= 0) {
            if (this.state === STATE.ROTATE)
                this.arcball.ProcessTouchUpEvent(new Vector2(0,0));
            this.state = STATE.NONE;
            if (this.m_gestureState === GESTURESTATE.UNKNOWN)
                this.endGestureDetection(GESTURESTATE.TAP);

            this.m_firstTouchPoint = null;
            this.m_touchPoints = [];
            this.dispatchEvent({type:'interactioncomplete'});
        }
        else if (this.m_touchPoints.length == 1) {
            const bOneFingerPan : boolean = this.enablePan && !this.enableRotate;
            if (!bOneFingerPan) {
                this.state = STATE.NONE;
                this.hand.ProcessEvent(new ManipEvent(timeStamp, ManipEventType.ET_Completed,
                    new Vector2(0, 0)));
            }
        }
        else {
            // Still 2+ fingers down - reset touch point array to allow continuing tracking with
            // remaining fingers.
        }
    }

    onTouchCancel = ( event: TouchEvent ) => {
        if ( this.enabled === false ) return;
        this.state = STATE.NONE;
    }

    //
    // Microsoft Edge: uses pointer events only, no mouse events.
    //
    // Why pointer? MS Edge has longtime support for pointer events, but still - as of late 2019, Edge 18 -
    // leaves the "touch events" checkbox as a disabled experimental setting.
    //
    // Edge also translates Precision Touchpad into finger touches. With one exception: two-finger pan
    // gets translated to a single-finger touchscreen action.
    //
    // https://blogs.windows.com/msedgedev/2017/12/07/better-precision-touchpad-experience-ptp-pointer-events/

    private m_pointerTrackpadDisambiguateTimer : number = undefined;
    private m_pointerTrackpadDisambiguateFirstEvent : PointerEvent = null;
    private m_bPointerTrackpadTwoFingerPanInProgress : boolean = false;
    onPointerTrackpadDisambiguate = () =>
    {
        // We didn't get a second pointerdown event, so this is a two-finger trackpad pan masking as a one-finger event.
        // Process a first touchdown event
        var event = this.m_pointerTrackpadDisambiguateFirstEvent;
        this.m_pointerTrackpadDisambiguateFirstEvent = null;
        this.m_touchPoints = [ new MCTouchPoint(event.clientX, event.clientY - 5, event.pointerId) ];
        this.onTouchStart_Impl(event.timeStamp);
        // Add a second simulated touchdown event.
        this.m_touchPoints.push(new MCTouchPoint(event.clientX, event.clientY + 5, event.pointerId + 0.5));
        this.onTouchStart_Impl(event.timeStamp);
        this.domElement.addEventListener('pointermove', this.onPointerMove, false);
        this.m_bPointerTrackpadTwoFingerPanInProgress = true;
        this.m_pointerTrackpadDisambiguateTimer = undefined;
    }
    onPointerDown = (event: PointerEvent) =>
    {
        // Suppress mousedown event
        event.preventDefault();

        if (event.pointerType == 'touch') {
            // Two-finger trackpad pans are translated to one-finger touches, with width=0.
            // Two-finger trackpad pinch/zooms come in as two separate finger touches, both with width=0.
            if (event.width == 0) {
                if (this.m_trackpadDetection != null) {
                    this.m_trackpadDetection = null;
                    this.m_bHasTrackpad = true;
                    console.log("Mouse type detection: trackpad, via gesture");
                }
                this.updateInstructions(INPUTTYPE.TRACKPAD);

                if (this.m_pointerTrackpadDisambiguateTimer === undefined) {
                    // Can't yet tell if this is a two-finger pan, or a two finger pinch/twist.
                    // Need to wait and see if a second pointevent comes in, usually immediately afterwards.
                    this.m_pointerTrackpadDisambiguateFirstEvent = event;
                    this.m_pointerTrackpadDisambiguateTimer = window.setTimeout(this.onPointerTrackpadDisambiguate, 50);
                } else {
                    // We got a second trackpad event within timeout. It's a pinch/twist.
                    window.clearTimeout(this.m_pointerTrackpadDisambiguateTimer);
                    this.m_pointerTrackpadDisambiguateTimer = undefined;
                    // Belatedly process the first touchdown event.
                    var event0 = this.m_pointerTrackpadDisambiguateFirstEvent;
                    this.m_pointerTrackpadDisambiguateFirstEvent = null;
                    this.m_touchPoints = [ new MCTouchPoint(event0.clientX, event0.clientY, event0.pointerId) ];
                    this.onTouchStart_Impl(event0.timeStamp);
                    this.m_touchPoints.push(new MCTouchPoint(event.clientX, event.clientY, event.pointerId));
                    this.onTouchStart_Impl(event.timeStamp);
                    this.domElement.addEventListener('pointermove', this.onPointerMove, false);
                }
            } else {
                this.m_touchPoints.push(new MCTouchPoint(event.clientX, event.clientY, event.pointerId));
                this.onTouchStart_Impl(event.timeStamp);
                this.updateInstructions(INPUTTYPE.TOUCH);
                if (this.m_touchPoints.length == 1)
                    this.domElement.addEventListener('pointermove', this.onPointerMove, false);
            }
        } else if (event.pointerType == 'mouse') {
            this.onMouseDown(event);
            this.domElement.addEventListener('pointermove', this.onPointerMove, false);
        }
        // TODO: cover pen + mouse
    }
    onPointerMove = (event: PointerEvent) =>
    {
        // Suppress mousemove event
        event.preventDefault();

        if (event.pointerType == 'touch') {
            const prevTouchPoints : MCTouchPoint[] = this.m_touchPoints;
            this.m_touchPoints = [];
            if (this.m_bPointerTrackpadTwoFingerPanInProgress) {
                this.m_touchPoints.push(new MCTouchPoint(event.clientX, event.clientY - 5, event.pointerId));
                this.m_touchPoints.push(new MCTouchPoint(event.clientX, event.clientY + 5, event.pointerId + 0.5));
            } else
                for (var touchPoint of prevTouchPoints)
                    if (touchPoint.m_identifier === event.pointerId)
                        this.m_touchPoints.push(new MCTouchPoint(event.clientX, event.clientY, event.pointerId));
                    else
                        this.m_touchPoints.push(touchPoint);
            this.onTouchMove_Impl(prevTouchPoints, event.timeStamp);
        } else if (event.pointerType == 'mouse') {
            this.onMouseMove(event);
        }
    }
    onPointerUp = (event: PointerEvent) => {
        // Suppress mouseup event
        event.preventDefault();
        if (event.pointerType == 'touch') {
            const prevTouchPoints : MCTouchPoint[] = this.m_touchPoints;
            if (this.m_bPointerTrackpadTwoFingerPanInProgress) {
                this.m_touchPoints = [ prevTouchPoints[0] ];
                this.onTouchEnd_Impl(event.timeStamp);
                this.m_touchPoints = [];
                this.m_bPointerTrackpadTwoFingerPanInProgress = false;                
                // Fall through and do second touchend.
            }
            else {
                this.m_touchPoints = [];
                for (var touchPoint of prevTouchPoints)
                    if (touchPoint.m_identifier != event.pointerId)
                        this.m_touchPoints.push(touchPoint);
            }
            this.onTouchEnd_Impl(event.timeStamp);
            if (this.m_touchPoints.length == 0)
                this.domElement.removeEventListener('pointermove', this.onPointerMove, false);
        }
        else if (event.pointerType == 'mouse') {
            this.onMouseUp(event);
            this.domElement.removeEventListener('pointermove', this.onPointerMove, false);
        }
    }

    // WebKit trackpad events:
    // - single finger touch moves cursor
    // - single finger click/drag triggers mouse events (onMouseDown etc.)
    // - two finger parallel movement invokes wheel (H or V)
    // - two finger pinch/rotate movement invokes these trackpad gesture events
    // - it's modal: either gesture (rotation/scaling) *or* translation, not both. Translation comes in with
    //   inertial events.
    private m_WebKitPrevRotation : number;
    private m_WebKitPrevScale : number;
    onWebKitTrackpadGestureStart = (event: GestureEvent) => {
        event.preventDefault();

        if (this.m_trackpadDetection != null) {
            this.m_bHasTrackpad = true;
            this.m_trackpadDetection = null;
            console.log("Mouse type detection: trackpad, via gesture");
        }

        this.m_WebKitPrevRotation = 0;
        this.m_WebKitPrevScale = 1;
        this.m_touchPoints = [ new MCTouchPoint(event.clientX, event.clientY) ];
        this.m_firstTouchPoint = this.m_touchPoints[0];
        this.state = STATE.DOLLY;
        this.hand.ProcessEvent(new ManipEvent(event.timeStamp, ManipEventType.ET_Starting, new Vector2(0, 0)));
        this.hand.ProcessEvent(new ManipEvent(event.timeStamp, ManipEventType.ET_Started, this.m_firstTouchPoint.m_position));
        this.updateInstructions(INPUTTYPE.TRACKPAD);
    }
    onWebKitTrackpadGestureChange = (event: GestureEvent) => {
        event.preventDefault();

        const prevCentroid = this.calcTouchCentroid();
        this.m_touchPoints = [ new MCTouchPoint(event.clientX, event.clientY) ];
        const centroid : Vector2 = this.m_touchPoints[0].m_position;
        const translate : Vector2 = centroid.clone().sub(prevCentroid); /// pchange
        this.hand.ProcessEvent(new ManipEvent(event.timeStamp, ManipEventType.ET_Moving, centroid,
            new ManipDelta(event.rotation - this.m_WebKitPrevRotation,
                centroid,
                translate.clone().negate(),
                1 / (event.scale / this.m_WebKitPrevScale)),
            false, false));
        this.m_WebKitPrevRotation = event.rotation;
        this.m_WebKitPrevScale = event.scale;
        if (this.m_gestureState === GESTURESTATE.UNKNOWN &&
            this.m_touchPoints[0].m_position.distanceTo(this.m_firstTouchPoint.m_position) > 20)
            this.endGestureDetection(GESTURESTATE.SWIPE);

        this.notifyIfCameraMoved();
    }
    onWebKitTrackpadGestureEnd = (event: GestureEvent) => {
        event.preventDefault();
        this.hand.ProcessEvent(new ManipEvent(event.timeStamp, ManipEventType.ET_Completed, new Vector2(0,0)));
        this.state = STATE.NONE;
        this.dispatchEvent({type:'interactioncomplete'});
    }
    onContextMenu = (event: MouseEvent) => {
        event.preventDefault();
    }
};
